import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useDealPromptPermission } from '@openx/components-deal-prompt/lib/hooks/useDealPromptPermission';
import { getDefaultDealPromptAudienceDealFormValues } from '@openx/components-deal-prompt/lib/utils/getDefaultDealPromptAudienceDealFormValues';
import {
  type DealPromptAudienceDealFormValues,
  EXPORT_TARGET_KEYS,
} from '@openx/components-deal-prompt/lib/utils/types';
import { Alert } from '@openx/design-system/components/Alert/Alert';
import { Button } from '@openx/design-system/components/Button/Button';
import { Checkbox } from '@openx/design-system/components/Checkbox/Checkbox';
import { FormControlLabel } from '@openx/design-system/components/Checkbox/FormControlLabel';
import { Dialog } from '@openx/design-system/components/Dialog/Dialog';
import { TextField } from '@openx/design-system/components/TextField/TextField';
import type {} from '@openx/design-system/theme/tokens/typography';
import { noop } from '@openx/utils/noop';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentModalContainer } from '../Modal/OpenXAgentModalContainerContext';

// Inside the OpenX Agent window the dialog and its backdrop are scoped to the window (above its resize handles, zIndex 3).
// Outside of it (no container) it falls back to the page, above the fixed OpenX Agent window (zIndex 1401).
const scopedDialogSx: SxProps<Theme> = {
  position: 'absolute',
  zIndex: 4,
  '& > .MuiBackdrop-root': { position: 'absolute' },
};
const pageDialogSx: SxProps<Theme> = { zIndex: 10001 };

const getExportTargetLabels = (t: TFunction) =>
  ({
    cookie: t('Cookies'),
    device: t('MAIDs'),
    idl: t('Ramp ID'),
    ctv: t('CTV'),
    ip: t('IP Address'),
    id5: t('ID5'),
  }) satisfies Record<keyof DealPromptAudienceDealFormValues['export_targets'], string>;

export type OpenXAgentSaveAudienceDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: DealPromptAudienceDealFormValues) => Promise<void>;
  dataPartnerAccountName?: string;
  isExplicitNonUsRegion?: boolean;
};

// OpenX Agent counterpart of DealPromptAudienceCreateDealDialog — same form logic, OpenX Agent copy and design-system UI.
// It only collects the values; creating the audience/deal is up to the caller's onSubmit.
export const OpenXAgentSaveAudienceDialog = ({
  open,
  onClose,
  onSubmit,
  dataPartnerAccountName,
  isExplicitNonUsRegion = false,
}: OpenXAgentSaveAudienceDialogProps) => {
  const { t } = useTranslation();
  const targetIdLabels = useMemo(() => getExportTargetLabels(t), [t]);
  const { isInternalUser } = useDealPromptPermission();
  const modalContainer = useOpenXAgentModalContainer();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<DealPromptAudienceDealFormValues>({
    defaultValues: getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion),
  });

  const hasName = !!watch('name')?.trim();

  useEffect(() => {
    if (open) {
      reset(getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion));
    }
  }, [open, isExplicitNonUsRegion, reset]);

  const handleClose = useCallback(() => {
    reset(getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion));
    onClose();
  }, [isExplicitNonUsRegion, onClose, reset]);

  const submit = handleSubmit(async values => {
    try {
      const valuesToSubmit = isExplicitNonUsRegion
        ? {
            ...values,
            export_targets: getDefaultDealPromptAudienceDealFormValues(true).export_targets,
          }
        : values;

      await onSubmit(valuesToSubmit);
      handleClose();
    } catch {
      noop;
    }
  });

  const showDataPartnerAlert = useMemo(() => {
    return dataPartnerAccountName && dataPartnerAccountName !== OPEN_AUDIENCE_ORG_NAME && isInternalUser;
  }, [dataPartnerAccountName, isInternalUser]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      title={t('Save your Audience')}
      description={t('To create a deal, please save your audience first.')}
      closeButtonLabel={t('Close')}
      container={modalContainer}
      disableScrollLock={!!modalContainer}
      sx={modalContainer ? scopedDialogSx : pageDialogSx}
      data-test="openx-agent-save-audience-dialog"
      secondaryActions={
        <Button variant="text" color="secondary" onClick={handleClose} data-test="openx-agent-save-audience-cancel">
          {t('Cancel')}
        </Button>
      }
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={submit}
          disabled={!hasName || isSubmitting}
          data-test="openx-agent-save-audience-submit"
        >
          {t('Save & Proceed')}
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {showDataPartnerAlert ? (
          <Alert severity="info" data-test="openx-agent-save-audience-partner-notice">
            {t('The audience and deal will be created under {partnerName}.', {
              partnerName: dataPartnerAccountName,
            })}
          </Alert>
        ) : null}

        <Controller
          name="name"
          control={control}
          rules={{
            required: t('Required'),
            validate: value => !!value.trim() || t('Required'),
          }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              slotProps={{ htmlInput: { ref } }}
              label={t('Audience Name')}
              placeholder={t('Placeholder text')}
              fullWidth
              error={!!error}
              helperText={error?.message}
              data-test="openx-agent-save-audience-name"
            />
          )}
        />

        {!isExplicitNonUsRegion ? (
          <Box data-test="openx-agent-save-audience-target-id-types">
            <Typography variant="overlineBold" component="h3" sx={{ display: 'block' }}>
              {t('Target ID Types')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('Select the ID types for your audience. Reach will be limited to your selected options.')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, max-content)',
                columnGap: 2,
                mt: 1,
              }}
            >
              {EXPORT_TARGET_KEYS.map(key => (
                <Controller
                  key={key}
                  name={`export_targets.${key}`}
                  control={control}
                  render={({ field: { value, onChange, onBlur, ref } }) => (
                    <FormControlLabel
                      label={targetIdLabels[key]}
                      data-test={`openx-agent-save-audience-target-${key}`}
                      control={
                        <Checkbox
                          checked={!!value}
                          onChange={event => onChange(event.target.checked)}
                          onBlur={onBlur}
                          slotProps={{ input: { ref } }}
                        />
                      }
                    />
                  )}
                />
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
};
